import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, X, Ruler, Layers, Clock } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { PROJECTS as DATA, PROJECT_CATEGORIES } from "@/data/urban";
import { PROJECTS as TID } from "@/constants/testIds";

export const Projects = ({ limit }) => {
  const [active, setActive] = useState("All");
  const [detail, setDetail] = useState(null);
  const filtered = useMemo(() => {
    let list = active === "All" ? DATA : DATA.filter((p) => p.category === active);
    return limit ? list.slice(0, limit) : list;
  }, [active, limit]);

  return (
    <section id="projects" className="py-20 md:py-28 bg-canvas">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <Reveal><div className="overline text-ink-soft">Featured Projects</div></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-ink leading-tight">Our interior &amp; renovation projects.</h2>
            </Reveal>
          </div>
          {limit && (
            <Link to="/projects" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-ink hover:opacity-70">
              View all projects
            </Link>
          )}
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {PROJECT_CATEGORIES.map((c) => (
            <button key={c} type="button" data-testid={TID.filter(c.toLowerCase().replace(/\s+/g, "-"))}
              onClick={() => setActive(c)}
              className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider border transition-colors ${
                active === c ? "bg-black text-white border-black" : "bg-white text-ink border-hairline hover:border-ink"
              }`}>{c}</button>
          ))}
        </div>

        <motion.div layout className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.button layout type="button" key={p.slug} data-testid={TID.card(i)} onClick={() => setDetail(p)}
                initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                className="group relative rounded-2xl overflow-hidden text-left bg-white border border-hairline">
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={p.img} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <span className="absolute top-4 left-4 bg-white text-black text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-md">{p.category}</span>
                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <h3 className="font-heading text-xl font-bold">{p.title}</h3>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-white/80"><MapPin className="h-3.5 w-3.5" /> {p.location}</div>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {detail && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDetail(null)}>
            <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              className="relative w-full max-w-5xl bg-white rounded-2xl overflow-hidden grid md:grid-cols-2 max-h-[88vh]" onClick={(e) => e.stopPropagation()}>
              <button type="button" data-testid={TID.lightboxClose} onClick={() => setDetail(null)} className="absolute top-4 right-4 z-10 h-10 w-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black">
                <X className="h-5 w-5" />
              </button>
              <img src={detail.img} alt={detail.title} className="h-64 md:h-full w-full object-cover" />
              <div className="p-7 sm:p-9 overflow-y-auto">
                <div className="overline text-ink-soft">{detail.category} · {detail.style}</div>
                <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-extrabold text-ink">{detail.title}</h3>
                <div className="mt-1 flex items-center gap-1.5 text-sm text-ink-soft"><MapPin className="h-4 w-4" /> {detail.location}</div>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  {[[Ruler, "Area", detail.area], [Layers, "Materials", detail.materials]].map(([Ic, k, v]) => (
                    <div key={k} className="rounded-xl border border-hairline p-3 col-span-3 sm:col-span-1">
                      <Ic className="h-4 w-4 text-ink-soft" />
                      <div className="mt-2 text-[10px] uppercase tracking-widest text-ink-soft">{k}</div>
                      <div className="text-sm font-semibold text-ink mt-0.5">{v}</div>
                    </div>
                  ))}
                </div>
                <p className="mt-5 text-ink-soft text-sm leading-relaxed">{detail.scope}</p>
                <Link to="/contact" onClick={() => setDetail(null)} className="mt-6 inline-flex items-center justify-center w-full bg-black text-white py-3 rounded-md font-bold uppercase tracking-wider text-sm hover:bg-ink transition-colors">
                  Get similar design
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
